import type { ScaleLinear } from 'd3-scale';
import { area, curveBumpX } from 'd3-shape';
import { animated } from '@react-spring/web';
import { useMentiSpring } from '@mentimeter/motion/spring';
import type { GuessTheNumberData } from './GuessTheNumber';

interface ScalesAreaProps {
  fill: string;
  correctAnswerColor: string;
  scores: GuessTheNumberData;
  xScale: ScaleLinear<number, number>;
  yScale: ScaleLinear<number, number>;
  paddingBottom?: number;
  disableAnimations?: boolean;
}

export const ResultsArea = ({
  fill,
  correctAnswerColor,
  scores,
  xScale,
  yScale,
  disableAnimations = false,
  paddingBottom = 0,
}: ScalesAreaProps) => {
  const scoresArray = Object.entries(scores)
    .map(([key, score]) => ({
      key: parseInt(key, 10),
      score,
    }))
    .sort((a, b) => a.key - b.key); // area generator expects sorted data

  const areaGenerator = area<{ score: number; key: number }>()
    .x((d) => xScale(d.key))
    .y1((d) => yScale(d.score))
    .y0(() => yScale(0))
    .curve(curveBumpX);

  const sameLengthArrayWithZeros = scoresArray.map((d) => ({
    key: d.key,
    score: 0,
  }));

  const pathStart = areaGenerator(sameLengthArrayWithZeros) || '';
  const path = areaGenerator(scoresArray) || '';

  const [styles] = useMentiSpring({
    from: { d: pathStart },
    to: { d: path },
    config: { tension: 280, friction: 30 },
    immediate: disableAnimations,
  });

  return (
    <>
      <g id="curve" style={{ transform: `translate(0, -${paddingBottom}px)` }}>
        <animated.path d={styles.d} fill={correctAnswerColor} />
      </g>
      <g
        data-testid="results-area"
        style={{ transform: `translate(0, -${paddingBottom}px)` }}
      >
        <animated.path d={styles.d} fill={fill} />
      </g>
    </>
  );
};
